import { DeleteIcon } from '@chakra-ui/icons';
import {
    Alert,
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    AlertIcon,
    Badge,
    Box,
    Button,
    Flex,
    HStack,
    IconButton,
    Select,
    Stat,
    StatLabel,
    StatNumber,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import {
    CellContext,
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from '@tanstack/react-table';
import PageCard from 'components/page-card';
import SortIndicator from 'components/table/SortIndicator';
import { useMemo, useRef, useState } from 'react';
import Loader from '../../../components/Loader';
import { translate, TranslationKeys } from '../../../i18n';
import { VideoSubmissionDto, VideoSubmissionStatus } from '../../../models/video-review/dto/video-submission.dto';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { statPlaceholder } from '../../analytics/utils/statValue';
import { useDeleteVideoSubmission } from '../hooks/useDeleteVideoSubmission';
import { useListVideoSubmissions } from '../hooks/useListVideoSubmissions';
import { tableProps, tableThProps, tableThFlexProps, tableCellProps, tableHeaderTextProps } from 'theme/constants';

const columnHelper = createColumnHelper<VideoSubmissionDto>();

export const statusColorMap: Record<VideoSubmissionStatus, string> = {
    [VideoSubmissionStatus.PENDING]: 'orange',
    [VideoSubmissionStatus.IN_REVIEW]: 'blue',
    [VideoSubmissionStatus.REVIEWED]: 'green',
};

const statusLabelMap: Record<VideoSubmissionStatus, TranslationKeys> = {
    [VideoSubmissionStatus.PENDING]: 'video_review.pending',
    [VideoSubmissionStatus.IN_REVIEW]: 'video_review.in_review',
    [VideoSubmissionStatus.REVIEWED]: 'video_review.reviewed',
};

function computeStats(submissions: VideoSubmissionDto[]) {
    const pending = submissions.filter((s) => s.status === VideoSubmissionStatus.PENDING).length;
    const inReview = submissions.filter((s) => s.status === VideoSubmissionStatus.IN_REVIEW).length;
    const reviewed = submissions.filter((s) => s.status === VideoSubmissionStatus.REVIEWED);

    let avgReviewDays: number | null = null;
    if (reviewed.length > 0) {
        const totalMs = reviewed.reduce((sum, s) => {
            const created = new Date(s.createdAt).getTime();
            const reviewedAt = s.reviewedAt ? new Date(s.reviewedAt).getTime() : created;
            return sum + (reviewedAt - created);
        }, 0);
        avgReviewDays = Math.round((totalMs / reviewed.length / (1000 * 60 * 60 * 24)) * 10) / 10;
    }

    return { pending, inReview, reviewed: reviewed.length, avgReviewDays };
}

export default function VideoSubmissionList() {
    const [statusFilter, setStatusFilter] = useState<VideoSubmissionStatus | undefined>(undefined);
    const { selectedOrganization } = useSelectedOrganization();
    const { data, isInitialLoading, isError } = useListVideoSubmissions(selectedOrganization?.id || '', statusFilter);
    const {
        data: allData,
        isInitialLoading: isAllInitialLoading,
        isError: isAllError,
    } = useListVideoSubmissions(selectedOrganization?.id || '');
    // `useListVideoSubmissions` is `enabled: !!organizationId` and this page
    // carried NO scope guard, so the raw `isLoading` it spun on stayed `true`
    // forever whenever no organization was selected (run 105).
    //
    // The two reads share that GATE and not their FAILURE, which is the half
    // run 105 got wrong and this file asserted for sixteen runs: the queryKey
    // is `[…, organizationId, status]`, so once the operator picks a status the
    // strip's unfiltered read is a SEPARATE request that can be refused on its
    // own — and `computeStats(allData || [])` then counted an absent answer as
    // four real zeros ("0 pending, 0 in review, 0 reviewed") beside a table of
    // rows that did arrive. A number carries no trace of its own absence
    // (D58(c)), so the strip is now classified on its own flags and says so.
    const readState = classifyRead(isError, isInitialLoading, data);
    const stripReadState = classifyRead(isAllError, isAllInitialLoading, allData);
    const stats = useMemo(() => (allData ? computeStats(allData) : undefined), [allData]);
    const statText = (value: number | undefined) =>
        value === undefined ? statPlaceholder(stripReadState === 'loading') : String(value);
    const [sorting, setSorting] = useState<SortingState>([]);
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const toast = useToast();

    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const [deletingSubmission, setDeletingSubmission] = useState<VideoSubmissionDto | null>(null);
    const cancelButtonRef = useRef<HTMLButtonElement>(null);
    const { mutateAsync: deleteSubmission, isLoading: isDeleting } = useDeleteVideoSubmission();

    const handleDeleteSubmission = (submission: VideoSubmissionDto) => {
        setDeletingSubmission(submission);
        onDeleteOpen();
    };

    const handleConfirmDelete = async () => {
        if (!deletingSubmission) return;
        try {
            await deleteSubmission(deletingSubmission.id);
            toast({
                title: translate('video_review.delete_success'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            onDeleteClose();
            setDeletingSubmission(null);
        } catch (error: any) {
            toast({
                title: translate('video_review.delete_error'),
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const navigateToDetail = (submissionId: string) => {
        window.location.hash = `#/admin/video-review-detail?submission=${submissionId}`;
    };

    const columns = useMemo(() => [
        columnHelper.accessor((row) => `${row.uploadedBy.firstName || ''} ${row.uploadedBy.lastName || ''}`.trim() || translate('common.unknown'), {
            id: 'player',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('video_review.player')}
                </Text>
            ),
            cell: (info: CellContext<VideoSubmissionDto, string>) => (
                <Text color={textColor} fontSize='sm' fontWeight='700'>
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('title', {
            id: 'title',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('video_review.title')}
                </Text>
            ),
            cell: (info: CellContext<VideoSubmissionDto, string>) => (
                <Text color={textColor} fontSize='sm' fontWeight='700' cursor='pointer' _hover={{ textDecoration: 'underline' }}
                    onClick={() => navigateToDetail(info.row.original.id)}>
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('sport', {
            id: 'sport',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('video_review.sport')}
                </Text>
            ),
            cell: (info: CellContext<VideoSubmissionDto, string>) => (
                <Text color={textColor} fontSize='sm'>
                    {info.getValue() || '-'}
                </Text>
            ),
        }),
        columnHelper.accessor('status', {
            id: 'status',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('video_review.status')}
                </Text>
            ),
            cell: (info: CellContext<VideoSubmissionDto, VideoSubmissionStatus>) => (
                <Badge colorScheme={statusColorMap[info.getValue()]}>
                    {translate(statusLabelMap[info.getValue()])}
                </Badge>
            ),
        }),
        columnHelper.accessor((row) => row.assignedTo?.email, {
            id: 'assignedTo',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('video_review.assigned_to')}
                </Text>
            ),
            cell: (info: CellContext<VideoSubmissionDto, string | undefined>) => (
                <Text color={textColor} fontSize='sm'>
                    {info.getValue() || '-'}
                </Text>
            ),
        }),
        columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('video_review.date')}
                </Text>
            ),
            cell: (info: CellContext<VideoSubmissionDto, string>) => (
                <Text color={textColor} fontSize='sm'>
                    {new Date(info.getValue()).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
                </Text>
            ),
        }),
        columnHelper.display({
            id: 'actions',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('common.actions')}
                </Text>
            ),
            cell: (info: CellContext<VideoSubmissionDto, unknown>) => (
                <Flex align='center' gap={2}>
                    <Button size='xs' colorScheme='brand' onClick={() => navigateToDetail(info.row.original.id)}>
                        {translate('video_review.view')}
                    </Button>
                    <IconButton
                        aria-label={translate('video_review.delete_submission')}
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleDeleteSubmission(info.row.original)}
                    />
                </Flex>
            ),
        }),
    ], [textColor]);

    const table = useReactTable({
        data: data || [],
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (readState === 'never-issued') {
        return (
            <PageCard title={translate('video_review.video_reviews')}>
                <ScopePrompt gate='organization' />
            </PageCard>
        );
    }

    if (readState === 'loading') {
        return <Loader isLoading />;
    }

    // `unreadable`, not the raw error object: a BACKGROUND refetch that fails
    // over rows already on screen must keep them rather than replace the whole
    // page with an alert (R96-3).
    if (readState === 'unreadable') {
        return (
            <Alert status="error">
                <AlertIcon />
                {translate('video_review.error_loading')}
            </Alert>
        );
    }

    return (
        <PageCard
            title={translate('video_review.video_reviews')}
            rightHeader={
                <Flex gap={2} align="center">
                    <Select
                        size='sm'
                        w='180px'
                        placeholder={translate('video_review.all_statuses')}
                        value={statusFilter || ''}
                        onChange={(e) => setStatusFilter(e.target.value as VideoSubmissionStatus || undefined)}
                    >
                        <option value={VideoSubmissionStatus.PENDING}>{translate('video_review.pending')}</option>
                        <option value={VideoSubmissionStatus.IN_REVIEW}>{translate('video_review.in_review')}</option>
                        <option value={VideoSubmissionStatus.REVIEWED}>{translate('video_review.reviewed')}</option>
                    </Select>
                </Flex>
            }
        >
            <HStack px='25px' mb="16px" spacing={6}>
                <Stat size="sm">
                    <StatLabel color="gray.400">{translate('video_review.pending')}</StatLabel>
                    <StatNumber color="orange.500">{statText(stats?.pending)}</StatNumber>
                </Stat>
                <Stat size="sm">
                    <StatLabel color="gray.400">{translate('video_review.in_review')}</StatLabel>
                    <StatNumber color="brand.500">{statText(stats?.inReview)}</StatNumber>
                </Stat>
                <Stat size="sm">
                    <StatLabel color="gray.400">{translate('video_review.reviewed')}</StatLabel>
                    <StatNumber color="green.500">{statText(stats?.reviewed)}</StatNumber>
                </Stat>
                <Stat size="sm">
                    <StatLabel color="gray.400">{translate('video_review.avg_review_time')}</StatLabel>
                    {/*
                      * Three outcomes, not two. `-` is the ANSWERED claim that
                      * nothing has been reviewed yet, so it must not double as
                      * the placeholder for a read that failed or never ran —
                      * that is `statPlaceholder`'s em dash.
                      */}
                    <StatNumber>
                        {stats === undefined
                            ? statPlaceholder(stripReadState === 'loading')
                            : stats.avgReviewDays != null
                                ? `${stats.avgReviewDays}d`
                                : '-'}
                    </StatNumber>
                </Stat>
            </HStack>
            <Box>
                <Table {...tableProps}>
                    <Thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <Th
                                        {...tableThProps}
                                        key={header.id}
                                        borderColor={borderColor}
                                        cursor='pointer'
                                        onClick={header.column.getToggleSortingHandler()}
                                    >
                                        <Flex
                                            {...tableThFlexProps}>
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            <SortIndicator direction={header.column.getIsSorted()} />
                                        </Flex>
                                    </Th>
                                ))}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map(row => (
                            <Tr key={row.id}>
                                {row.getVisibleCells().map(cell => (
                                    <Td
                                        key={cell.id}
                                        {...tableCellProps}
                                        borderColor={borderColor}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </Td>
                                ))}
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
                {table.getRowModel().rows.length === 0 && (
                    <Text textAlign="center" color="gray.400" py={8}>
                        {translate('video_review.no_submissions')}
                    </Text>
                )}
            </Box>

            <AlertDialog
                isOpen={isDeleteOpen}
                leastDestructiveRef={cancelButtonRef}
                onClose={onDeleteClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                            {translate('video_review.delete_confirm')}
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            {translate('video_review.delete_confirm_message')}
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelButtonRef} onClick={onDeleteClose}>
                                {translate('common.cancel')}
                            </Button>
                            <Button colorScheme='red' onClick={handleConfirmDelete} ml={3} isLoading={isDeleting}>
                                {translate('common.delete')}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </PageCard>
    );
}
